/**
 * Deutsche Website-Texte. Leitsprache: der Inhalt stammt aus dem
 * Corporate-Identity- und Landingpage-Dokument des M.E.-Kollektivs,
 * content.ts ist die englische Übersetzung davon.
 *
 * Die Texte selbst liegen als JSON in src/content/ und werden dort über die
 * Redaktionsoberfläche unter /admin bearbeitet. Dieses Modul gibt ihnen nur
 * ihre Namen und ihre Typen — es steht bewusst zwischen JSON und Seiten, damit
 * ein Umbau der Ablage keine einzige Komponente berührt.
 *
 * Jede Seiten-Datei trägt zusätzlich `extraBlocks`: frei gesetzte Abschnitte,
 * die die Redaktion an eine bestehende Seite anhängen kann (siehe blocks.ts).
 */

import orgJson from '../../content/org.json';
import displayJson from '../../content/display.json';
import homeJson from '../../content/home.json';
import organisationJson from '../../content/organisation.json';
import mitmachenJson from '../../content/mitmachen.json';
import socialJson from '../../content/social.json';
import podcastJson from '../../content/podcast.json';
import legalJson from '../../content/legal.json';

export interface PodcastPlatform {
  name: string;
  url: string;
}

export interface PodcastEpisode {
  title: string;
  date: string;
  duration: string;
  description: string;
  url: string;
}

/**
 * Leaves the shape of `extraBlocks` open. The sections an editor adds are read
 * back through `toBlocks`, which validates them at runtime, so nothing here
 * depends on what is in them - while their JSON type does: an empty list types
 * as `never[]`, a list with one section as the shape of exactly that section.
 * Both languages are compared against each other in content.ts, and without
 * this a section added in German alone - or a text block here and an image
 * block there - would fail that comparison. That would turn a normal edit in
 * the editing interface into a build error, which is precisely what the
 * comparison is not there for: it guards the texts that pages read by name.
 */
const withOpenBlocks = <T extends { extraBlocks: unknown }>(
  page: T,
): Omit<T, 'extraBlocks'> & { extraBlocks: unknown[] } => ({
  ...page,
  extraBlocks: page.extraBlocks as unknown[],
});

export const ORG_INFO_DE = orgJson.de;
export const DISPLAY_DE = displayJson.de;
export const HOME_CONTENT_DE = withOpenBlocks(homeJson.de);
export const ORGANISATION_DE = withOpenBlocks(organisationJson.de);
export const MITMACHEN_DE = withOpenBlocks(mitmachenJson.de);
export const SOCIAL_MEDIA_DE = withOpenBlocks(socialJson.de);
export const LEGAL_CONTENT_DE = legalJson.de;

// While the lists are empty the JSON gives them no element type, so the two
// casts state what the podcast page may expect once entries are added.
export const PODCAST_DE = {
  ...withOpenBlocks(podcastJson.de),
  platforms: { ...podcastJson.de.platforms, items: podcastJson.de.platforms.items as PodcastPlatform[] },
  episodes: { ...podcastJson.de.episodes, items: podcastJson.de.episodes.items as PodcastEpisode[] },
};

export const CONTENT_DE = {
  org: ORG_INFO_DE,
  home: HOME_CONTENT_DE,
  organisation: ORGANISATION_DE,
  mitmachen: MITMACHEN_DE,
  socialMedia: SOCIAL_MEDIA_DE,
  podcast: PODCAST_DE,
  legal: LEGAL_CONTENT_DE,
  display: DISPLAY_DE,
};
